/** @jsx jsx */

import { Fragment, PureComponent } from 'react';

import { Icon } from '@zapier/zinnia-icons';
import { Colors } from '@zapier/design-system';
import { RelativeTime } from '@zapier/date';
import { jsx, css } from '@emotion/core';

import Tag from 'app/common/components/Tag';

import type { Event as IssueEvent } from 'app/developer-v3/hooks/useIssue';

const Styles = {
  root: ({ precedesComment, followsComment }) => css`
    border-left: 4px solid ${Colors.GrayWarm4};
    gap: 4px;
    padding-top: ${followsComment ? 25 : 10}px;
    padding-bottom: ${precedesComment ? 25 : 10}px;
    padding-left: 20px;
    padding-right: 20px;
    transform: translateX(50px);
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
  `,
};

type Props = {
  event: IssueEvent;
  precedesComment: boolean;
  followsComment: boolean;
};

const EventUser = props => {
  if (!props.user) {
    return null;
  }

  return props.user.isStaff ? (
    <Fragment>
      Zapier <Icon color="Red4" name="zapierLogo" size={30} />
    </Fragment>
  ) : (
    props.user.name || null
  );
};

const StatusChangeEvent = props => {
  if (props.user) {
    return (
      <Fragment>
        {props.tag}
        <span> by </span>
        <EventUser user={props.user} />
      </Fragment>
    );
  } else {
    return (
      <Fragment>
        {props.tag}
        <span> automatically</span>
      </Fragment>
    );
  }
};

const ClosedEvent = props => {
  const tag = <Tag kind="upcoming">Closed</Tag>;
  return <StatusChangeEvent tag={tag} user={props.event.user} />;
};

const ReOpenedEvent = props => {
  const tag = <Tag kind="success">Re-Opened</Tag>;
  return <StatusChangeEvent tag={tag} user={props.event.user} />;
};

const RequestedCloseEvent = props => {
  const tag = <Tag kind="premium">Review Requested</Tag>;
  return <StatusChangeEvent tag={tag} user={props.event.user} />;
};

const ToggledTagEvent = props => {
  if (props.user) {
    return (
      <Fragment>
        <EventUser user={props.user} /> {props.verb} {props.tag}
      </Fragment>
    );
  } else {
    return (
      <Fragment>
        {props.tag}
        <span> {props.verb} automatically</span>
      </Fragment>
    );
  }
};

const AddedTagEvent = props => {
  const tag = (
    <Tag backgroundColor={props.event.contentObject.backgroundColor}>
      {props.event.contentObject.name}
    </Tag>
  );
  return <ToggledTagEvent tag={tag} user={props.event.user} verb="added" />;
};

const RemovedTagEvent = props => {
  const tag = (
    <Tag backgroundColor={props.event.contentObject.backgroundColor}>
      {props.event.contentObject.name}
    </Tag>
  );
  return <ToggledTagEvent tag={tag} user={props.event.user} verb="removed" />;
};

const SetAssigneeEvent = props => {
  if (props.event.contentObject) {
    const el = props.event.user ? (
      <Fragment>
        {' '}
        <EventUser user={props.event.user} /> assigned this Issue to{' '}
        {props.event.contentObject.name}
      </Fragment>
    ) : (
      <Fragment>
        {props.event.contentObject.name} was automatically assigned to this
        Issue
      </Fragment>
    );

    return el;
  } else {
    const el = props.event.user ? (
      <Fragment>
        {' '}
        <EventUser user={props.event.user} /> removed this Issue’s assignee
      </Fragment>
    ) : (
      'Assignee was automatically removed'
    );

    return <span>{el}</span>;
  }
};

class Event extends PureComponent<Props> {
  getTypeOf = () => {
    return this.props.event?.typeOf ?? 0;
  };

  getComponent = (typeOf: number) => {
    // Defined in `zapier/loki issues.models.Event.TYPES`
    return {
      '1': ClosedEvent,
      '2': ReOpenedEvent,
      '3': AddedTagEvent,
      '4': RemovedTagEvent,
      '5': SetAssigneeEvent,
      '6': RequestedCloseEvent,
    }[typeOf];
  };

  render() {
    const { event, followsComment, precedesComment } = this.props;
    const Component = this.getComponent(this.getTypeOf());
    return Component ? (
      <div css={Styles.root({ followsComment, precedesComment })}>
        <Component event={event} />
        &nbsp;
        <RelativeTime date={event.createdAt} />
      </div>
    ) : null;
  }
}

export default Event;
